import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ifMatchFor } from '../api/client';
import { qk, useDevices, useRooms, useRoutines } from '../api/queries';
import type { Mode, Phase, PhaseIn, Routine, Trigger, Weekday } from '../api/types';
import { WEEKDAYS } from '../api/types';
import { useAction } from '../components/feedback';
import { Async, Badge, Card, Field, PageHeader } from '../components/ui';
import { byId, coverage, deviceName, forecastDay, isRadiator, roomName, triggerOf } from '../lib/domain';
import { MODE_LABEL, WEEKDAY_SHORT } from '../lib/labels';
import { hhmm, parseHHMM } from '../lib/time';
import { MODES } from '../api/types';

export function RoutinesPage() {
  const { routineId } = useParams();
  const routines = useRoutines();
  const nav = useNavigate();
  const list = routines.data ?? [];
  const selected = list.find((r) => r.id === routineId) ?? null;

  useEffect(() => {
    if (!routineId && list.length) nav(`/routines/${list[0].id}`, { replace: true });
  }, [routineId, list, nav]);

  return (
    <>
      <PageHeader title="Routines" subtitle="Toute modification valide est appliquée immédiatement aux radiateurs concernés." />
      <div className="split split-left">
        <aside className="split-side">
          <RoutineList routines={list} loading={routines.isLoading} error={routines.error} selectedId={routineId} />
        </aside>
        <div className="split-main stack-l">
          {selected ? <RoutineEditor key={`${selected.id}:${selected.version}`} routine={selected} /> : !routines.isLoading && <Card><p className="muted">Créez une première routine.</p></Card>}
          <CoverageMatrix routines={list} />
        </div>
      </div>
    </>
  );
}

function RoutineList({ routines, loading, error, selectedId }: { routines: Routine[]; loading: boolean; error: unknown; selectedId?: string }) {
  const { run, busy } = useAction();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const rooms = useRooms();
  const devices = useDevices();
  const roomMap = byId(rooms.data);
  const devMap = byId(devices.data);

  const target = (r: Routine) =>
    (r.assignments ?? []).map((a) => (a.target_type === 'room' ? `Pièce : ${roomName(roomMap, a.target_id)}` : `Radiateur : ${deviceName(devMap.get(a.target_id))}`)).join(' · ') || 'Non assignée';

  return (
    <div className="stack">
      <Async q={{ data: routines, isLoading: loading, error }} empty={<p className="muted small">Aucune routine.</p>}>
        {(list) => (
          <ul className="pick-list" aria-label="Routines">
            {list.map((r) => (
              <li key={r.id}>
                <Link to={`/routines/${r.id}`} className={`pick ${r.id === selectedId ? 'is-on' : ''}`} aria-current={r.id === selectedId ? 'true' : undefined}>
                  <span className="row between"><span className="strong">{r.name}</span><Badge tone={r.active ? 'ok' : 'neutral'}>{r.active ? 'Active' : 'Inactive'}</Badge></span>
                  <span className="small muted">{r.weekdays.length ? r.weekdays.map((d) => WEEKDAY_SHORT[d as Weekday]).join(' ') : 'aucun jour'} · {r.phases?.length ?? 0} phase(s)</span>
                  <span className="xsmall muted">{target(r)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Async>
      <form className="card stack-s" onSubmit={(e) => {
        e.preventDefault();
        run(() => api.send<Routine>('POST', '/routines', { body: { name: name.trim(), weekdays: [], active: false } }).then((r) => r.data), {
          success: 'Routine créée (inactive)', invalidate: [qk.routines],
        }).then((r) => { if (r) { setName(''); nav(`/routines/${r.id}`); } });
      }}>
        <Field label="Nouvelle routine" htmlFor="new-routine"><input id="new-routine" placeholder="ex. Semaine" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <button className="btn" disabled={busy || !name.trim()}>Créer</button>
      </form>
    </div>
  );
}

interface Draft {
  key: string;
  name: string;
  mode: Mode;
  trigger: Trigger;
  presence: boolean;
  departure: boolean;
  scene: boolean;
  webhook: string;
  active: boolean;
}

const toDraft = (p: Phase): Draft => {
  const g = (p.geofencing ?? {}) as Record<string, boolean>;
  const sn = (p.scene_notification ?? null) as { enabled?: boolean; webhook_url?: string } | null;
  return {
    key: p.id, name: p.name ?? '', mode: p.mode, trigger: triggerOf(p), active: p.active,
    presence: !!g.start_requires_presence, departure: !!g.end_on_departure,
    scene: !!sn?.enabled, webhook: sn?.webhook_url ?? '',
  };
};

function toPhaseIn(d: Draft, i: number): PhaseIn {
  const isWake = d.trigger.type === 'reveil';
  return {
    order: i + 1,
    name: d.name.trim() || null,
    mode: d.mode,
    active: d.active,
    start_trigger: d.trigger,
    geofencing: isWake ? { start_requires_presence: false, end_on_departure: false } : { start_requires_presence: d.presence, end_on_departure: d.departure },
    scene_notification: isWake && d.scene ? { enabled: true, webhook_url: d.webhook.trim() } : null,
  };
}

function validate(drafts: Draft[]): string[] {
  const errs: string[] = [];
  const wakes = drafts.filter((d) => d.trigger.type === 'reveil');
  if (wakes.length > 1) errs.push('Une seule phase Réveil par routine.');
  const times = drafts.flatMap((d) => (d.trigger.type === 'heure_fixe' ? [d.trigger.time] : []));
  if (new Set(times).size !== times.length) errs.push('Deux phases à heure fixe ont la même heure.');
  for (const d of drafts) {
    if (d.trigger.type === 'heure_fixe' && parseHHMM(d.trigger.time) === null) errs.push('Heure au format HH:mm attendue.');
    if (d.trigger.type === 'reveil') {
      if (d.trigger.preheat_minutes < 0 || d.trigger.preheat_minutes > 59) errs.push('Préchauffage entre 0 et 59 minutes.');
      if (d.trigger.duration_minutes < 0 || d.trigger.duration_minutes > 180) errs.push('Durée après réveil entre 0 et 180 minutes.');
      if (d.scene && !/^https?:\/\//.test(d.webhook.trim())) errs.push('URL de scène requise (http:// ou https://).');
    }
  }
  return [...new Set(errs)];
}

function RoutineEditor({ routine }: { routine: Routine }) {
  const { run, busy } = useAction();
  const nav = useNavigate();
  const path = `/routines/${routine.id}`;
  const ifMatch = () => ifMatchFor(path, routine.version);
  const [name, setName] = useState(routine.name);
  const [days, setDays] = useState<Weekday[]>(routine.weekdays as Weekday[]);
  const initial = useMemo(() => [...(routine.phases ?? [])].sort((a, b) => a.order - b.order).map(toDraft), [routine.phases]);
  const [drafts, setDrafts] = useState<Draft[]>(initial);
  const [wakeSim, setWakeSim] = useState('07:15');
  const dirtyPhases = JSON.stringify(drafts) !== JSON.stringify(initial);
  const errors = validate(drafts);
  const segments = forecastDay(drafts.map((d, i) => ({ ...toPhaseIn(d, i), id: d.key, routine_id: routine.id, geofencing: {}, start_trigger: d.trigger } as unknown as Phase)), parseHHMM(wakeSim));

  const update = (i: number, patch: Partial<Draft>) => setDrafts((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const move = (i: number, dir: -1 | 1) => setDrafts((ds) => {
    const j = i + dir;
    if (j < 0 || j >= ds.length) return ds;
    const c = [...ds];
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });
  const add = (type: Trigger['type']) => setDrafts((ds) => [...ds, {
    key: `new-${Date.now()}`, name: '', mode: 'eco', active: true, presence: false, departure: false, scene: false, webhook: '',
    trigger: type === 'reveil' ? { type: 'reveil', preheat_minutes: 45, duration_minutes: 45 } : { type: 'heure_fixe', time: '08:00' },
  }]);

  const inv = [qk.routines, qk.state];

  return (
    <Card>
      <div className="stack">
        <form className="row gap wrap align-end" onSubmit={(e) => {
          e.preventDefault();
          run(() => api.send('PATCH', path, { body: { name: name.trim(), weekdays: days }, ifMatch: ifMatch() }), { success: 'Routine enregistrée', invalidate: inv });
        }}>
          <Field label="Nom de la routine" htmlFor="r-name"><input id="r-name" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <fieldset className="field">
            <legend className="label">Jours</legend>
            <div className="row gap-xs wrap">
              {WEEKDAYS.map((d) => (
                <button key={d} type="button" className={`day ${days.includes(d) ? 'is-on' : ''}`} aria-pressed={days.includes(d)}
                  onClick={() => setDays((xs) => (xs.includes(d) ? xs.filter((x) => x !== d) : WEEKDAYS.filter((w) => xs.includes(w) || w === d)))}>
                  {WEEKDAY_SHORT[d]}
                </button>
              ))}
            </div>
          </fieldset>
          <button className="btn" disabled={busy || (name === routine.name && days.join() === routine.weekdays.join())}>Enregistrer</button>
          <label className="check">
            <input type="checkbox" checked={routine.active} disabled={busy}
              onChange={(e) => run(() => api.send('PATCH', path, { body: { active: e.target.checked }, ifMatch: ifMatch() }), { success: e.target.checked ? 'Routine activée' : 'Routine désactivée', invalidate: inv })} />
            Active
          </label>
        </form>

        <section className="stack-s" aria-labelledby="h-day">
          <div className="row between wrap gap-s">
            <h3 id="h-day" className="sub-title">Déroulé prévu d’une journée</h3>
            <label className="row gap-s small">Réveil simulé <input type="time" value={wakeSim} onChange={(e) => setWakeSim(e.target.value)} className="input-s" /></label>
          </div>
          <div className="day-bar" role="img" aria-label={segments.map((s) => `${MODE_LABEL[s.mode]} dès ${hhmm(s.start)}`).join(', ') || 'aucune phase active'}>
            {segments.map((s, i) => (
              <div key={i} className={`day-seg mode-${s.mode}`} style={{ width: `${((s.end - s.start) / 1440) * 100}%` }} title={`${s.label} · ${hhmm(s.start)}`}>
                <span>{MODE_LABEL[s.mode]}</span>
                <span className="mono xsmall">{s.carried ? 'veille' : hhmm(s.start)}</span>
              </div>
            ))}
          </div>
          <div className="day-axis mono xsmall muted" aria-hidden="true"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
          <p className="xsmall muted">Prévision locale : ne tient compte ni de la présence, ni des forçages, absences ou mesures.</p>
        </section>

        <section className="stack-s" aria-labelledby="h-phases">
          <h3 id="h-phases" className="sub-title">Phases</h3>
          {drafts.length === 0 && <p className="empty">Aucune phase : la routine reste inactive tant qu’elle est vide.</p>}
          {drafts.map((d, i) => (
            <div key={d.key} className={`phase ${d.active ? '' : 'is-off'}`}>
              <div className="phase-head">
                <span className="mono strong">{i + 1}</span>
                <input aria-label={`Nom de la phase ${i + 1}`} className="input-s grow" placeholder={d.trigger.type === 'reveil' ? 'Réveil' : 'Nom (optionnel)'} value={d.name} onChange={(e) => update(i, { name: e.target.value })} />
                <button type="button" className="btn btn-s" aria-label="Monter" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                <button type="button" className="btn btn-s" aria-label="Descendre" onClick={() => move(i, 1)} disabled={i === drafts.length - 1}>↓</button>
                <label className="check small"><input type="checkbox" checked={d.active} onChange={(e) => update(i, { active: e.target.checked })} />Active</label>
                <button type="button" className="btn btn-s btn-danger" aria-label={`Supprimer la phase ${i + 1}`} onClick={() => setDrafts((ds) => ds.filter((_, j) => j !== i))}>Suppr.</button>
              </div>
              <div className="phase-body">
                <Field label="Mode" htmlFor={`m-${d.key}`}>
                  <select id={`m-${d.key}`} value={d.mode} onChange={(e) => update(i, { mode: e.target.value as Mode })}>
                    {MODES.map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                  </select>
                </Field>
                {d.trigger.type === 'heure_fixe' ? (
                  <>
                    <Field label="Début" htmlFor={`t-${d.key}`}>
                      <input id={`t-${d.key}`} type="time" value={d.trigger.time} onChange={(e) => update(i, { trigger: { type: 'heure_fixe', time: e.target.value } })} />
                    </Field>
                    <div className="field">
                      <span className="label">Présence</span>
                      <label className="check small"><input type="checkbox" checked={d.presence} onChange={(e) => update(i, { presence: e.target.checked })} />Démarre seulement si présent</label>
                      <label className="check small"><input type="checkbox" checked={d.departure} onChange={(e) => update(i, { departure: e.target.checked })} />Se termine au départ</label>
                    </div>
                  </>
                ) : (
                  <>
                    <Field label="Préchauffage (min)" htmlFor={`p-${d.key}`} hint="0 à 59">
                      <input id={`p-${d.key}`} type="number" min={0} max={59} value={d.trigger.preheat_minutes}
                        onChange={(e) => update(i, { trigger: { ...(d.trigger as Extract<Trigger, { type: 'reveil' }>), preheat_minutes: +e.target.value } })} />
                    </Field>
                    <Field label="Après le réveil (min)" htmlFor={`du-${d.key}`} hint="0 à 180">
                      <input id={`du-${d.key}`} type="number" min={0} max={180} value={d.trigger.duration_minutes}
                        onChange={(e) => update(i, { trigger: { ...(d.trigger as Extract<Trigger, { type: 'reveil' }>), duration_minutes: +e.target.value } })} />
                    </Field>
                    <div className="field">
                      <label className="check small"><input type="checkbox" checked={d.scene} onChange={(e) => update(i, { scene: e.target.checked })} />Scène à l’heure du réveil</label>
                      {d.scene && <input aria-label="URL de la scène" className="mono" placeholder="http://192.168.88.10:51828/reveil-on" value={d.webhook} onChange={(e) => update(i, { webhook: e.target.value })} />}
                      <span className="hint">Indépendante de la présence.</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="row gap-s wrap">
            <button type="button" className="btn btn-s" onClick={() => add('heure_fixe')}>+ Phase à heure fixe</button>
            <button type="button" className="btn btn-s" onClick={() => add('reveil')} disabled={drafts.some((d) => d.trigger.type === 'reveil')}>+ Phase Réveil</button>
          </div>
          {errors.length > 0 && <ul className="alert alert-bad small" role="alert">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
          <div className="row gap-s wrap">
            <button type="button" className="btn btn-primary" disabled={busy || !dirtyPhases || errors.length > 0}
              onClick={() => run(() => api.send('PUT', `${path}/phases`, { body: drafts.map(toPhaseIn), ifMatch: ifMatch() }), {
                success: drafts.length ? 'Phases enregistrées et appliquées' : 'Phases supprimées : routine désactivée', invalidate: inv,
              })}>
              Enregistrer les phases
            </button>
            {dirtyPhases && <button type="button" className="btn" onClick={() => setDrafts(initial)}>Annuler les changements</button>}
          </div>
        </section>

        <Assignments routine={routine} />

        <div className="divider" />
        <button type="button" className="btn btn-danger" disabled={busy}
          onClick={() => {
            if (!window.confirm(`Supprimer la routine « ${routine.name} » et toutes ses assignations ?`)) return;
            run(() => api.send('DELETE', path, { ifMatch: ifMatch() }), { success: 'Routine supprimée', invalidate: inv }).then(() => nav('/routines'));
          }}>
          Supprimer la routine
        </button>
      </div>
    </Card>
  );
}

function Assignments({ routine }: { routine: Routine }) {
  const { run, busy } = useAction();
  const rooms = useRooms();
  const devices = useDevices();
  const roomMap = byId(rooms.data);
  const devMap = byId(devices.data);
  const [target, setTarget] = useState('');
  const path = `/routines/${routine.id}`;
  const assigned = routine.assignments ?? [];
  const taken = new Set(assigned.map((a) => `${a.target_type}:${a.target_id}`));

  return (
    <section className="stack-s" aria-labelledby="h-assign">
      <h3 id="h-assign" className="sub-title">Assignations</h3>
      {assigned.length === 0 && <p className="small muted">Non assignée : cette routine ne pilote aucun radiateur.</p>}
      {assigned.map((a) => (
        <div key={`${a.target_type}:${a.target_id}`} className="row between gap-s">
          <span>{a.target_type === 'room' ? 'Pièce' : 'Radiateur'} · <span className="strong">{a.target_type === 'room' ? roomName(roomMap, a.target_id) : deviceName(devMap.get(a.target_id))}</span></span>
          <button type="button" className="btn btn-s" disabled={busy}
            onClick={() => run(() => api.send('DELETE', `${path}/assignments/${a.target_type}/${a.target_id}`, { ifMatch: ifMatchFor(path, routine.version) }), { success: 'Assignation retirée', invalidate: [qk.routines, qk.state] })}>
            Retirer
          </button>
        </div>
      ))}
      <form className="row gap-s wrap align-end" onSubmit={(e) => {
        e.preventDefault();
        const [type, id] = target.split(':');
        run(() => api.send('POST', `${path}/assign`, { body: type === 'room' ? { room_id: id } : { device_id: id }, ifMatch: ifMatchFor(path, routine.version) }), {
          success: 'Routine assignée', invalidate: [qk.routines, qk.state],
        }).then(() => setTarget(''));
      }}>
        <Field label="Assigner à" htmlFor="assign">
          <select id="assign" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choisir…</option>
            <optgroup label="Pièces">
              {(rooms.data ?? []).filter((r) => !taken.has(`room:${r.id}`)).map((r) => <option key={r.id} value={`room:${r.id}`}>{r.name}</option>)}
            </optgroup>
            <optgroup label="Radiateurs">
              {(devices.data ?? []).filter(isRadiator).filter((d) => !taken.has(`device:${d.id}`)).map((d) => <option key={d.id} value={`device:${d.id}`}>{deviceName(d)}</option>)}
            </optgroup>
          </select>
        </Field>
        <button className="btn" disabled={busy || !target}>Assigner</button>
      </form>
      <p className="xsmall muted">Refusé si les jours chevauchent une autre routine assignée directement au même radiateur ou à la même pièce. Un chevauchement radiateur / pièce est normal : le radiateur l’emporte.</p>
    </section>
  );
}

function CoverageMatrix({ routines }: { routines: Routine[] }) {
  const devices = useDevices();
  const radiators = (devices.data ?? []).filter(isRadiator);
  if (!radiators.length) return null;
  return (
    <Card title="Qui suit quoi, jour par jour" actions={<span className="small muted">radiateur, puis pièce, sinon dernier mode ou Arrêt</span>}>
      <div className="table-scroll">
        <table className="matrix">
          <thead><tr><th scope="col">Radiateur</th>{WEEKDAYS.map((d) => <th key={d} scope="col">{WEEKDAY_SHORT[d]}</th>)}</tr></thead>
          <tbody>
            {radiators.map((d) => (
              <tr key={d.id}>
                <th scope="row">{deviceName(d)}</th>
                {coverage(d, routines).map((c, i) => (
                  <td key={i} className={`cov cov-${c.source ?? 'none'}`}>
                    <span className="strong">{c.routine?.name ?? 'Aucune'}</span>
                    <span className="xsmall">{c.source === 'device' ? 'radiateur' : c.source === 'room' ? 'pièce' : 'repli'}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
